import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import {
  Robot, ArrowLeft, Sparkle, PaperPlaneTilt, Trash, Envelope,
  ChatText, UsersThree, CheckCircle, Clock, PencilSimple, X, MagnifyingGlass, Lightning
} from '@phosphor-icons/react';

const STATUS_STYLES = {
  pending: 'bg-neutral-200 text-neutral-700',
  drafted: 'bg-amber-100 text-amber-800',
  sent: 'bg-blue-100 text-blue-800',
  joined: 'bg-emerald-100 text-emerald-800',
};

const AdminOutreach = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [defaultType, setDefaultType] = useState('contractor');
  const [channels, setChannels] = useState({ email: true, sms: true });
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [findTrade, setFindTrade] = useState('');
  const [findCity, setFindCity] = useState('');
  const [findMode, setFindMode] = useState('city'); // 'city' | 'state'
  const [findKind, setFindKind] = useState('contractor'); // 'contractor' | 'ad_sales'
  const [candidates, setCandidates] = useState([]);
  const [checked, setChecked] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [campTrade, setCampTrade] = useState('');
  const [campCity, setCampCity] = useState('');
  const [campFreq, setCampFreq] = useState('weekly');
  const [recruiterStatus, setRecruiterStatus] = useState(null);

  const loadRecruiter = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/recruiter/runs');
      setRecruiterStatus(r.data);
    } catch {}
  }, []);

  const runRecruiter = async () => {
    setBusy('recruiter');
    try {
      const res = await axiosInstance.post('/recruiter/run');
      toast({ title: 'Recruiter started', description: res.data.message });
      setTimeout(() => { loadRecruiter(); load(); }, 45000);
    } catch (e) {
      toast({ title: 'Could not start recruiter', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [s, p, c] = await Promise.all([
        axiosInstance.get('/outreach/stats'),
        axiosInstance.get('/outreach/prospects'),
        axiosInstance.get('/outreach/campaigns'),
      ]);
      setStats(s.data.stats ? { ...s.data.stats, email_configured: s.data.email_configured, sms_configured: s.data.sms_configured } : null);
      setProspects(p.data.prospects || []);
      setCampaigns(c.data.campaigns || []);
    } catch (e) {
      if (e.response?.status === 403) navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => { load(); loadRecruiter(); }, [load, loadRecruiter]);

  const selectedChannels = Object.keys(channels).filter((c) => channels[c]);

  const findProspects = async () => {
    if (!findTrade.trim() || !findCity.trim()) {
      return toast({ title: `Enter a trade and a ${findMode}`, variant: 'destructive' });
    }
    setBusy('find');
    setCandidates([]);
    try {
      const payload = { trade: findTrade, limit: 10, kind: findKind };
      if (findMode === 'state') payload.state = findCity; else payload.city = findCity;
      const res = await axiosInstance.post('/outreach/find', payload, { timeout: 240000 });
      const list = res.data.candidates || [];
      setCandidates(list);
      const pre = {};
      list.forEach((c, i) => { pre[i] = true; });
      setChecked(pre);
      toast({ title: `Found ${list.length} businesses` });
    } catch (e) {
      toast({ title: 'Web search failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const importSelected = async () => {
    const selected = candidates.filter((_, i) => checked[i]);
    if (selected.length === 0) return toast({ title: 'Select at least one business', variant: 'destructive' });
    setBusy('import-found');
    try {
      const res = await axiosInstance.post('/outreach/find/import', { candidates: selected });
      toast({
        title: `Added ${res.data.added} prospects to the outreach list`,
        description: res.data.auto_finding > 0
          ? `Hunting contact info for ${res.data.auto_finding} of them in the background — refresh in a minute.`
          : undefined,
      });
      setCandidates([]);
      setChecked({});
      load();
    } catch (e) {
      toast({ title: 'Import failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const createCampaign = async () => {
    if (!campTrade.trim() || !campCity.trim()) return toast({ title: 'Enter a trade and a city', variant: 'destructive' });
    setBusy('create-campaign');
    try {
      await axiosInstance.post('/outreach/campaigns', { trade: campTrade, cities: [campCity], frequency: campFreq, limit: 10 });
      toast({ title: 'Auto-pilot campaign created' });
      setCampTrade(''); setCampCity('');
      load();
    } catch (e) {
      toast({ title: 'Could not create campaign', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const toggleCampaign = async (id) => {
    try { await axiosInstance.patch(`/outreach/campaigns/${id}`); load(); }
    catch (e) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const runCampaignNow = async (id) => {
    setBusy(`run-${id}`);
    try {
      const res = await axiosInstance.post(`/outreach/campaigns/${id}/run-now`, {}, { timeout: 120000 });
      toast({ title: `Queued ${res.data.added} new prospects` });
      load();
    } catch (e) {
      toast({ title: 'Run failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this auto-pilot campaign?')) return;
    try { await axiosInstance.delete(`/outreach/campaigns/${id}`); load(); }
    catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };


  const importPaste = async () => {
    if (!pasteText.trim()) return;    setBusy('import');
    try {
      const res = await axiosInstance.post('/outreach/prospects/paste', { text: pasteText, default_type: defaultType });
      toast({
        title: `Imported ${res.data.added} prospects`,
        description: res.data.auto_finding > 0
          ? `Hunting contact info for ${res.data.auto_finding} of them in the background — refresh in a minute.`
          : undefined,
      });
      setPasteText('');
      load();
    } catch (e) {
      toast({ title: 'Import failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const draftOne = async (id) => {
    setBusy(`draft-${id}`);
    try {
      await axiosInstance.post(`/outreach/prospects/${id}/draft`);
      toast({ title: 'AI draft ready' });
      load();
    } catch (e) {
      toast({ title: 'Draft failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const [draftProgress, setDraftProgress] = useState(null);

  const pollDraftJob = async () => {
    for (let i = 0; i < 400; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const r = await axiosInstance.get('/outreach/draft-status');
        const j = r.data.job;
        if (!j) break;
        if (j.status !== 'running') {
          toast({ title: `Drafting finished: ${j.done} drafted${j.failed ? `, ${j.failed} failed` : ''}` });
          break;
        }
        setDraftProgress(`${j.done}/${j.total || '…'}`);
      } catch (e) { break; }
    }
    setDraftProgress(null);
    load();
  };

  const draftAll = async () => {
    setBusy('draft-all');
    try {
      const res = await axiosInstance.post('/outreach/generate-all');
      if (res.data.already_running) {
        toast({ title: 'A drafting job is already running', description: 'Progress will show below.' });
      } else {
        toast({ title: 'AI drafting started', description: 'Runs in the background — progress shows on the button.' });
      }
      await pollDraftJob();
    } catch (e) {
      toast({ title: 'Bulk draft failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const redraftAll = async () => {
    setBusy('redraft-all');
    try {
      const res = await axiosInstance.post('/outreach/redraft-all');
      if (res.data.already_running) {
        toast({ title: 'A drafting job is already running', description: 'Progress will show below.' });
      } else {
        toast({ title: 'Re-drafting started', description: 'Runs in the background — progress shows on the button.' });
      }
      await pollDraftJob();
    } catch (e) {
      toast({ title: 'Re-draft failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const findContactOne = async (id) => {
    setBusy(`findcontact-${id}`);
    try {
      const res = await axiosInstance.post(`/outreach/prospects/${id}/find-contact`);
      const d = res.data;
      toast({
        title: d.updated ? 'Contact info found!' : 'Nothing new found',
        description: `Checked ${d.pages_checked} page${d.pages_checked === 1 ? '' : 's'} — email: ${d.email || '—'}, phone: ${d.phone || '—'}`,
      });
      load();
    } catch (e) {
      toast({ title: 'Contact search failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const findContactsAll = async () => {
    setBusy('findcontacts-all');
    try {
      const res = await axiosInstance.post('/outreach/find-contacts-all');
      if (res.data.already_running) {
        toast({ title: 'A background job is already running', description: 'Wait for it to finish, then try again.' });
      } else {
        toast({ title: 'Contact hunt started', description: 'Crawling prospect websites in the background.' });
      }
      for (let i = 0; i < 400; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const r = await axiosInstance.get('/outreach/draft-status');
          const j = r.data.job;
          if (!j) break;
          if (j.status !== 'running') {
            toast({ title: `Contact hunt finished: found info for ${j.found || 0} of ${j.done} prospects` });
            break;
          }
          setDraftProgress(`${j.done}/${j.total || '…'}`);
        } catch (e) { break; }
      }
      setDraftProgress(null);
      load();
    } catch (e) {
      toast({ title: 'Contact hunt failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const sendOne = async (id) => {
    if (selectedChannels.length === 0) return toast({ title: 'Pick at least one channel', variant: 'destructive' });
    setBusy(`send-${id}`);
    try {
      const res = await axiosInstance.post(`/outreach/prospects/${id}/send`, { channels: selectedChannels });
      const r = res.data.result;
      toast({ title: `Sent (email: ${r.email || '—'}, sms: ${r.sms || '—'})` });
      load();
    } catch (e) {
      toast({ title: 'Send failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const sendAll = async () => {
    if (selectedChannels.length === 0) return toast({ title: 'Pick at least one channel', variant: 'destructive' });
    if (!window.confirm(`Send all drafted invites via ${selectedChannels.join(' + ')}?`)) return;
    setBusy('send-all');
    try {
      const res = await axiosInstance.post('/outreach/send-all', { channels: selectedChannels });
      toast({ title: `Sent ${res.data.sent}${res.data.failed ? `, ${res.data.failed} failed` : ''}` });
      load();
    } catch (e) {
      toast({ title: 'Bulk send failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const remove = async (id) => {
    try {
      await axiosInstance.delete(`/outreach/prospects/${id}`);
      load();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const saveDraftEdit = async () => {
    // local-only edit persisted by resending on save via draft endpoint not needed;
    // simply patch the fields through send flow — store edits directly
    setBusy('save-edit');
    try {
      await axiosInstance.post(`/outreach/prospects/${editDraft.id}/save-draft`, {
        email_subject: editDraft.email_subject,
        email_body: editDraft.email_body,
        sms_body: editDraft.sms_body,
      });
      toast({ title: 'Draft updated' });
      setEditDraft(null);
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const statCards = stats ? [
    { label: 'Prospects', value: stats.total, icon: UsersThree, color: 'text-neutral-700' },
    { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-neutral-500' },
    { label: 'Drafted', value: stats.drafted, icon: Sparkle, color: 'text-amber-600' },
    { label: 'Sent', value: stats.sent, icon: PaperPlaneTilt, color: 'text-blue-600' },
    { label: 'Joined', value: stats.joined, icon: CheckCircle, color: 'text-emerald-600' },
    { label: 'Conversion', value: `${stats.conversion_rate}%`, icon: Robot, color: 'text-[#D95800]' },
  ] : [];

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="admin-outreach-page">
      {/* Header */}
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-white hover:text-[#D95800]" data-testid="back-to-admin-btn">
              <ArrowLeft size={24} weight="bold" />
            </button>
            <div className="w-12 h-12 bg-[#D95800] border-2 border-white flex items-center justify-center">
              <Robot size={26} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white uppercase">AI Outreach Agent</h1>
              <p className="text-neutral-400 text-sm">Recruit contractors & homeowners with AI-personalized invites</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="outreach-stats">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border-2 border-black p-4 shadow-[3px_3px_0px_#000]">
              <s.icon size={20} weight="bold" className={s.color} />
              <p className="text-2xl font-black mt-1">{s.value}</p>
              <p className="text-xs font-bold uppercase text-neutral-500">{s.label}</p>
            </div>
          ))}
        </div>

        {(stats && (!stats.email_configured || !stats.sms_configured)) && (
          <div className="bg-amber-50 border-2 border-amber-400 p-3 text-sm font-medium text-amber-900" data-testid="channel-warning">
            {!stats.email_configured && 'Email (Resend) is not configured. '}
            {!stats.sms_configured && 'SMS (Twilio) is not configured.'}
          </div>
        )}

        {/* Contractor Recruiter Agent */}
        <div className="bg-neutral-900 border-2 border-black p-6 shadow-[4px_4px_0px_#D95800]" data-testid="recruiter-agent-section">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-black uppercase text-lg mb-1 flex items-center gap-2 text-white">
                <Robot size={20} weight="bold" className="text-[#D95800]" /> Contractor Recruiter Agent
              </h2>
              <p className="text-sm text-neutral-400 max-w-xl">
                Watches new marketplace leads, finds real contractors in that city via web search, and queues
                personalized invites below for your review. Runs automatically every day.
              </p>
              {recruiterStatus && (
                <p className="text-xs font-bold uppercase text-neutral-500 mt-2" data-testid="recruiter-status">
                  {recruiterStatus.pending_leads} unprocessed lead{recruiterStatus.pending_leads === 1 ? '' : 's'}
                  {recruiterStatus.runs?.[0] && ` · last run queued ${recruiterStatus.runs[0].prospects_queued} prospect(s) from ${recruiterStatus.runs[0].leads_processed} lead(s)`}
                </p>
              )}
            </div>
            <button
              onClick={runRecruiter}
              disabled={busy === 'recruiter'}
              className="px-6 py-3 bg-[#D95800] text-white font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-1 active:shadow-none disabled:opacity-50"
              data-testid="recruiter-run-btn"
            >
              {busy === 'recruiter' ? 'Running…' : 'Run Now'}
            </button>
          </div>
        </div>

        {/* Web Lead Finder */}
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_#000]" data-testid="lead-finder-section">
          <h2 className="font-black uppercase text-lg mb-1 flex items-center gap-2">
            <MagnifyingGlass size={20} weight="bold" className="text-[#D95800]" /> 1 · Find Prospects on the Web
          </h2>
          <p className="text-sm text-neutral-500 mb-4">
            The AI Lead Finder searches the web for contractor businesses, visits their websites, and pulls
            public contact info. Review the results, then add the good ones to your outreach list.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={findTrade}
              onChange={(e) => setFindTrade(e.target.value)}
              placeholder={findKind === 'ad_sales' ? 'Business type (e.g. building material supplier)' : 'Trade (e.g. roofing)'}
              className="h-11 px-3 border-2 border-black font-bold w-64"
              data-testid="find-trade-input"
            />
            <select
              value={findKind}
              onChange={(e) => { setFindKind(e.target.value); setFindTrade(''); }}
              className="h-11 px-2 border-2 border-black font-bold"
              data-testid="find-kind-select"
            >
              <option value="contractor">Recruit Contractors</option>
              <option value="ad_sales">Sell Ad Space</option>
            </select>
            <select
              value={findMode}
              onChange={(e) => { setFindMode(e.target.value); setFindCity(''); }}
              className="h-11 px-2 border-2 border-black font-bold"
              data-testid="find-mode-select"
            >
              <option value="city">By City</option>
              <option value="state">By State</option>
            </select>
            <input
              value={findCity}
              onChange={(e) => setFindCity(e.target.value)}
              placeholder={findMode === 'state' ? 'State (e.g. New Jersey or NJ)' : 'City (e.g. Austin TX)'}
              className="h-11 px-3 border-2 border-black font-bold w-56"
              data-testid="find-city-input"
            />
            <Button
              onClick={findProspects}
              disabled={busy === 'find'}
              className="h-11 bg-neutral-900 text-white font-black uppercase border-2 border-black shadow-[3px_3px_0px_#D95800] rounded-none"
              data-testid="find-prospects-btn"
            >
              <MagnifyingGlass size={18} weight="bold" className="mr-2" />
              {busy === 'find' ? 'Searching the web…' : findMode === 'state' ? 'Search State' : 'Search Web'}
            </Button>
          </div>

          {busy === 'find' && (
            <p className="mt-4 text-sm font-bold text-neutral-500 animate-pulse" data-testid="find-loading">
              {findMode === 'state'
                ? 'Sweeping the state — searching statewide plus its largest cities. This takes ~60–90 seconds…'
                : 'Searching, visiting business websites, and extracting contacts — this takes ~20 seconds…'}
            </p>
          )}

          {candidates.length > 0 && (
            <div className="mt-5">
              <div className="overflow-x-auto border-2 border-neutral-300">
                <table className="w-full text-sm" data-testid="candidates-table">
                  <thead>
                    <tr className="bg-neutral-100 text-left">
                      <th className="p-2 w-8"></th>
                      <th className="p-2 font-black uppercase text-xs">Business</th>
                      <th className="p-2 font-black uppercase text-xs">Email</th>
                      <th className="p-2 font-black uppercase text-xs">Phone</th>
                      <th className="p-2 font-black uppercase text-xs">Website</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c, i) => (
                      <tr key={i} className={`border-t border-neutral-200 ${c.already_known ? 'opacity-50' : ''}`} data-testid={`candidate-row-${i}`}>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={!!checked[i]}
                            onChange={(e) => setChecked((prev) => ({ ...prev, [i]: e.target.checked }))}
                            disabled={!c.email && !c.phone}
                            data-testid={`candidate-check-${i}`}
                          />
                        </td>
                        <td className="p-2 font-bold">
                          {c.name}
                          {c.already_known && <span className="ml-2 px-1.5 py-0.5 text-[10px] font-black uppercase bg-neutral-200 rounded">Already known</span>}
                          {!c.email && !c.phone && <span className="ml-2 px-1.5 py-0.5 text-[10px] font-black uppercase bg-red-100 text-red-600 rounded">No contact found</span>}
                        </td>
                        <td className="p-2 text-xs">{c.email || '—'}</td>
                        <td className="p-2 text-xs">{c.phone || '—'}</td>
                        <td className="p-2 text-xs">
                          <a href={c.website} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                            {c.website?.replace(/^https?:\/\/(www\.)?/, '').slice(0, 30)}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                onClick={importSelected}
                disabled={busy === 'import-found'}
                className="mt-3 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000] rounded-none"
                data-testid="import-selected-btn"
              >
                {busy === 'import-found' ? 'Adding…' : `Add ${Object.values(checked).filter(Boolean).length} Selected to Prospects`}
              </Button>
            </div>
          )}
        </div>

        {/* Auto-Pilot Campaigns */}
        <div className="bg-neutral-900 border-2 border-black p-6 shadow-[4px_4px_0px_#D95800]" data-testid="autopilot-section">
          <h2 className="font-black uppercase text-lg mb-1 flex items-center gap-2 text-white">
            <Lightning size={20} weight="fill" className="text-[#D95800]" /> Auto-Pilot Campaigns
          </h2>
          <p className="text-sm text-neutral-400 mb-4">
            Save a trade + one or more cities and the agent will search the web automatically (weekly or daily) and
            queue fresh contractor prospects for you — no duplicates. For a nationwide sweep, list several cities
            separated by commas.
          </p>
          <div className="flex flex-wrap items-end gap-3 mb-5">
            <div>
              <label className="block text-[10px] font-black uppercase text-neutral-500 mb-1">Trade</label>
              <input
                value={campTrade}
                onChange={(e) => setCampTrade(e.target.value)}
                placeholder="e.g. electrician"
                className="h-11 px-3 border-2 border-black font-bold w-52 bg-white"
                data-testid="campaign-trade-input"
              />
            </div>
            <div className="flex-1 min-w-[260px]">
              <label className="block text-[10px] font-black uppercase text-neutral-500 mb-1">Cities (comma-separated for a sweep)</label>
              <input
                value={campCity}
                onChange={(e) => setCampCity(e.target.value)}
                placeholder="e.g. Austin TX, Dallas TX, Houston TX, Phoenix AZ"
                className="h-11 px-3 border-2 border-black font-bold w-full bg-white"
                data-testid="campaign-city-input"
              />
            </div>
            <select
              value={campFreq}
              onChange={(e) => setCampFreq(e.target.value)}
              className="h-11 px-3 border-2 border-black font-bold bg-white"
              data-testid="campaign-freq-select"
            >
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
            <Button
              onClick={createCampaign}
              disabled={busy === 'create-campaign'}
              className="h-11 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000] rounded-none"
              data-testid="create-campaign-btn"
            >
              {busy === 'create-campaign' ? 'Creating…' : 'Add Auto-Pilot'}
            </Button>
          </div>

          {campaigns.length > 0 && (
            <div className="space-y-2" data-testid="campaigns-list">
              {campaigns.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center gap-3 bg-neutral-800 border border-neutral-700 p-3" data-testid={`campaign-row-${c.id}`}>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-bold text-white capitalize">
                      {c.trade}
                      {(c.cities?.length || 0) > 1
                        ? <span className="ml-2 text-xs font-black uppercase bg-[#D95800] text-white px-1.5 py-0.5 rounded">{c.cities.length} cities</span>
                        : <span className="text-neutral-300"> · {c.cities?.[0] || c.city}</span>}
                    </p>
                    <p className="text-xs text-neutral-400">
                      {(c.cities?.length || 0) > 1 && <span className="text-neutral-300">{c.cities.join(', ')} · </span>}
                      {c.frequency} · {c.total_added} queued all-time · last run {c.last_run_at ? new Date(c.last_run_at).toLocaleDateString() : 'never'} · next {c.next_run_at ? new Date(c.next_run_at).toLocaleDateString() : '—'}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-black uppercase rounded ${c.active ? 'bg-emerald-100 text-emerald-800' : 'bg-neutral-600 text-neutral-300'}`}>
                    {c.active ? 'Active' : 'Paused'}
                  </span>
                  <button
                    onClick={() => runCampaignNow(c.id)}
                    disabled={busy === `run-${c.id}`}
                    className="px-3 py-1.5 text-xs font-bold bg-white hover:bg-neutral-100 border border-black rounded"
                    data-testid={`run-campaign-btn-${c.id}`}
                  >
                    {busy === `run-${c.id}` ? 'Running…' : 'Run Now'}
                  </button>
                  <button
                    onClick={() => toggleCampaign(c.id)}
                    className="px-3 py-1.5 text-xs font-bold bg-neutral-200 hover:bg-neutral-300 border border-neutral-400 rounded"
                    data-testid={`toggle-campaign-btn-${c.id}`}
                  >
                    {c.active ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    onClick={() => deleteCampaign(c.id)}
                    className="px-2 py-1.5 text-xs font-bold bg-red-50 hover:bg-red-100 border border-red-300 text-red-600 rounded"
                    data-testid={`delete-campaign-btn-${c.id}`}
                  >
                    <Trash size={14} className="inline" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>



        {/* Import */}
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_#000]">
          <h2 className="font-black uppercase text-lg mb-1">2 · Or Paste Your Own List</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Paste one prospect per line: <code className="bg-neutral-100 px-1">name, email, phone, city, trade</code>.
            Add "homeowner" or "contractor" in a row to override the default type. Only contact people who have
            opted in or that you have a business relationship with.
          </p>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={5}
            placeholder={'Mike Johnson, mike@buildco.com, 512-555-0134, Austin TX, roofing\nSarah Lee, sarah@gmail.com, , Dallas TX, homeowner'}
            className="w-full border-2 border-neutral-300 rounded p-3 font-mono text-sm focus:outline-none focus:border-[#D95800]"
            data-testid="paste-prospects-input"
          />
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <select
              value={defaultType}
              onChange={(e) => setDefaultType(e.target.value)}
              className="h-10 px-3 border-2 border-black font-bold bg-white"
              data-testid="default-type-select"
            >
              <option value="contractor">Default: Contractors</option>
              <option value="homeowner">Default: Homeowners</option>
            </select>
            <Button
              onClick={importPaste}
              disabled={busy === 'import' || !pasteText.trim()}
              className="h-10 bg-neutral-900 text-white font-bold uppercase border-2 border-black shadow-[3px_3px_0px_#D95800] rounded-none"
              data-testid="import-prospects-btn"
            >
              {busy === 'import' ? 'Importing…' : 'Import Prospects'}
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_#000]">
          <h2 className="font-black uppercase text-lg mb-4">3 · Draft & Send</h2>
          <div className="flex flex-wrap items-center gap-4">
            <Button
              onClick={draftAll}
              disabled={busy === 'draft-all'}
              className="h-11 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000] rounded-none"
              data-testid="draft-all-btn"
            >
              <Sparkle size={18} weight="fill" className="mr-2" />
              {busy === 'draft-all' ? `AI Drafting… ${draftProgress || ''}` : 'AI Draft All Pending'}
            </Button>
            <Button
              onClick={redraftAll}
              disabled={busy === 'redraft-all'}
              className="h-11 bg-white hover:bg-amber-100 text-black font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000] rounded-none"
              data-testid="redraft-all-btn"
            >
              <Sparkle size={18} weight="bold" className="mr-2" />
              {busy === 'redraft-all' ? `Re-Drafting… ${draftProgress || ''}` : 'Re-Draft All (New Pitch)'}
            </Button>
            <Button
              onClick={findContactsAll}
              disabled={busy === 'findcontacts-all'}
              className="h-11 bg-[#2B4C6F] hover:bg-[#1F3A57] text-white font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000] rounded-none"
              data-testid="find-contacts-all-btn"
            >
              <MagnifyingGlass size={18} weight="bold" className="mr-2" />
              {busy === 'findcontacts-all' ? `Hunting… ${draftProgress || ''}` : 'Find Missing Contacts'}
            </Button>
            <div className="flex items-center gap-3 px-3 h-11 border-2 border-neutral-300">
              <label className="flex items-center gap-1 font-bold text-sm cursor-pointer">
                <input type="checkbox" checked={channels.email} onChange={(e) => setChannels((c) => ({ ...c, email: e.target.checked }))} data-testid="channel-email-checkbox" />
                <Envelope size={16} weight="bold" /> Email
              </label>
              <label className="flex items-center gap-1 font-bold text-sm cursor-pointer">
                <input type="checkbox" checked={channels.sms} onChange={(e) => setChannels((c) => ({ ...c, sms: e.target.checked }))} data-testid="channel-sms-checkbox" />
                <ChatText size={16} weight="bold" /> SMS
              </label>
            </div>
            <Button
              onClick={sendAll}
              disabled={busy === 'send-all'}
              className="h-11 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000] rounded-none"
              data-testid="send-all-btn"
            >
              <PaperPlaneTilt size={18} weight="fill" className="mr-2" />
              {busy === 'send-all' ? 'Sending…' : 'Send All Drafted'}
            </Button>
          </div>
        </div>

        {/* Prospect table */}
        <div className="bg-white border-2 border-black shadow-[4px_4px_0px_#000] overflow-x-auto">
          <table className="w-full text-sm" data-testid="prospects-table">
            <thead>
              <tr className="bg-neutral-900 text-white text-left">
                <th className="p-3 font-black uppercase text-xs">Prospect</th>
                <th className="p-3 font-black uppercase text-xs">Type</th>
                <th className="p-3 font-black uppercase text-xs">Contact</th>
                <th className="p-3 font-black uppercase text-xs">Status</th>
                <th className="p-3 font-black uppercase text-xs text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {prospects.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-neutral-400 font-bold">No prospects yet — paste a list above to get started</td></tr>
              )}
              {prospects.map((p) => (
                <React.Fragment key={p.id}>
                  <tr className="border-t border-neutral-200 hover:bg-neutral-50" data-testid={`prospect-row-${p.id}`}>
                    <td className="p-3">
                      <p className="font-bold">{p.name}</p>
                      <p className="text-xs text-neutral-500">{[p.city, p.trade].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 text-xs font-black uppercase rounded ${p.prospect_type === 'contractor' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>
                        {p.prospect_type}
                      </span>
                    </td>
                    <td className="p-3 text-xs">
                      {p.email && <p className="flex items-center gap-1"><Envelope size={12} /> {p.email}</p>}
                      {p.phone && <p className="flex items-center gap-1"><ChatText size={12} /> {p.phone}</p>}
                      {!p.email && !p.phone && <p className="text-neutral-400 italic">no contact yet</p>}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 text-xs font-black uppercase rounded ${STATUS_STYLES[p.status] || STATUS_STYLES.pending}`} data-testid={`status-${p.id}`}>
                        {p.status}
                      </span>
                      {p.sent_channels?.length > 0 && (
                        <p className="text-[10px] text-neutral-400 mt-0.5">via {p.sent_channels.join(' + ')}</p>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {p.website && (!p.email || !p.phone) && (
                        <button
                          onClick={() => findContactOne(p.id)}
                          disabled={busy === `findcontact-${p.id}`}
                          className="px-2 py-1 text-xs font-bold bg-blue-100 hover:bg-blue-200 border border-blue-400 rounded mr-1"
                          title="Find contact info from their website"
                          data-testid={`find-contact-btn-${p.id}`}
                        >
                          {busy === `findcontact-${p.id}` ? '…' : <MagnifyingGlass size={14} weight="bold" className="inline" />}
                        </button>
                      )}
                      <button
                        onClick={() => draftOne(p.id)}
                        disabled={busy === `draft-${p.id}`}
                        className="px-2 py-1 text-xs font-bold bg-amber-100 hover:bg-amber-200 border border-amber-400 rounded mr-1"
                        title="AI draft"
                        data-testid={`draft-btn-${p.id}`}
                      >
                        {busy === `draft-${p.id}` ? '…' : <Sparkle size={14} weight="fill" className="inline" />}
                      </button>
                      {(p.email_body || p.sms_body) && (
                        <>
                          <button
                            onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                            className="px-2 py-1 text-xs font-bold bg-neutral-100 hover:bg-neutral-200 border border-neutral-300 rounded mr-1"
                            data-testid={`view-draft-btn-${p.id}`}
                          >
                            <PencilSimple size={14} className="inline" />
                          </button>
                          <button
                            onClick={() => sendOne(p.id)}
                            disabled={busy === `send-${p.id}`}
                            className="px-2 py-1 text-xs font-bold bg-[#D95800] text-white hover:bg-[#E55D00] border border-black rounded mr-1"
                            data-testid={`send-btn-${p.id}`}
                          >
                            {busy === `send-${p.id}` ? '…' : <PaperPlaneTilt size={14} weight="fill" className="inline" />}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => remove(p.id)}
                        className="px-2 py-1 text-xs font-bold bg-red-50 hover:bg-red-100 border border-red-300 text-red-600 rounded"
                        data-testid={`delete-btn-${p.id}`}
                      >
                        <Trash size={14} className="inline" />
                      </button>
                    </td>
                  </tr>
                  {expanded === p.id && (
                    <tr className="bg-neutral-50 border-t border-neutral-200">
                      <td colSpan={5} className="p-4" data-testid={`draft-preview-${p.id}`}>
                        {editDraft?.id === p.id ? (
                          <div className="space-y-2">
                            <input
                              value={editDraft.email_subject || ''}
                              onChange={(e) => setEditDraft((d) => ({ ...d, email_subject: e.target.value }))}
                              className="w-full border-2 border-neutral-300 rounded p-2 text-sm font-bold"
                              placeholder="Email subject"
                              data-testid="edit-subject-input"
                            />
                            <textarea
                              value={editDraft.email_body || ''}
                              onChange={(e) => setEditDraft((d) => ({ ...d, email_body: e.target.value }))}
                              rows={4}
                              className="w-full border-2 border-neutral-300 rounded p-2 text-sm"
                              placeholder="Email body"
                              data-testid="edit-body-input"
                            />
                            <textarea
                              value={editDraft.sms_body || ''}
                              onChange={(e) => setEditDraft((d) => ({ ...d, sms_body: e.target.value }))}
                              rows={2}
                              className="w-full border-2 border-neutral-300 rounded p-2 text-sm"
                              placeholder="SMS text"
                              data-testid="edit-sms-input"
                            />
                            <div className="flex gap-2">
                              <Button onClick={saveDraftEdit} disabled={busy === 'save-edit'} className="h-9 bg-emerald-600 text-white font-bold rounded-none border-2 border-black" data-testid="save-draft-btn">
                                {busy === 'save-edit' ? 'Saving…' : 'Save Draft'}
                              </Button>
                              <Button onClick={() => setEditDraft(null)} variant="outline" className="h-9 rounded-none border-2 border-neutral-400 font-bold">
                                <X size={14} className="mr-1" /> Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-black uppercase text-neutral-500 mb-1">Email</p>
                              <p className="font-bold text-sm">{p.email_subject || '—'}</p>
                              <p className="text-sm whitespace-pre-line mt-1">{p.email_body || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-black uppercase text-neutral-500 mb-1">SMS</p>
                              <p className="text-sm">{p.sms_body || '—'}</p>
                              <button
                                onClick={() => setEditDraft({ id: p.id, email_subject: p.email_subject, email_body: p.email_body, sms_body: p.sms_body })}
                                className="mt-3 px-3 py-1 text-xs font-bold bg-neutral-200 hover:bg-neutral-300 rounded"
                                data-testid={`edit-draft-btn-${p.id}`}
                              >
                                <PencilSimple size={12} className="inline mr-1" /> Edit draft
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-neutral-400">
          Compliance note: only message people who opted in or with whom you have an existing business relationship.
          Every email includes an unsubscribe link; SMS recipients can reply STOP (handled by Twilio).
        </p>
      </div>
    </div>
  );
};

export default AdminOutreach;
